import { useLocation, useSearchParams } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import {
  SidebarSection,
  SidebarItem,
  SidebarLabel,
  SidebarDivider,
  SidebarSpacer,
} from '@/components/catalyst-ui/sidebar'
import { Badge } from '@/components/catalyst-ui/badge'
import { CollapsibleSidebarItem } from '@/components/ui/CollapsibleSidebarItem'
import { AICreditsCard } from '@/components/ui/AICreditsCard'
import { useUIStore } from '@/state/ui.store'
import { buildUrl, buildWorkspaceUrl, getWorkspaceProjectFromParams } from '@/lib/utils'
import { useProjects } from '@/features/workspace/hooks/useProjects'
import { useWorkspaceDetails } from '@/features/workspace/hooks/useWorkspaceDetails'
import { useSelectionStore } from '@/state/selection.store'
import { useAiCompanionMode } from '@/features/site-pages/hooks/useAiCompanionMode'
import { useSidebar } from '@/components/ui/HybridLayout'
import { FormBuilderIcon } from '@/assets/icons'

import { RectangleGroupIcon, 
         ChartBarIcon, 
         DocumentDuplicateIcon, 
         DocumentTextIcon, 
         UsersIcon, 
         Cog6ToothIcon, 
         PuzzlePieceIcon, 
         ChevronLeftIcon,
         PhotoIcon,
       } from '@heroicons/react/24/solid'

export function ProjectSidebar() {
  const location = useLocation()
  const { isSectionExpanded, toggleSection } = useUIStore()
  const [searchParams] = useSearchParams()
  const { workspace, project } = getWorkspaceProjectFromParams(searchParams)
  const { projects } = useProjects(workspace)
  const { workspace: workspaceDetails } = useWorkspaceDetails({ workspaceId: workspace })
  const { setCurrentProject, setProjects, currentProject } = useSelectionStore(state => ({
    setCurrentProject: state.setCurrentProject,
    setProjects: state.setProjects,
    currentProject: state.currentProject,
  }))
  const uniqueUrl = currentProject?.uniqueUrl
  const isProjectLicensed = !!currentProject?.order?.paid
  const { isAiCompanionMode } = useAiCompanionMode(uniqueUrl)
  const sidebar = useSidebar()
  const closeSidebar = sidebar?.closeSidebar || (() => {})
  const isSharedProject = searchParams.get('shared') === 'true'
  const hideCredits = Boolean(workspaceDetails?.hideCredits)

  const prevRef = useRef({ projects: null, project: null })
  
  useEffect(() => {
    if (!projects?.length) return
    
    const normalized = projects.map(p => ({ ...p, id: p._id || p.id }))
    
    if (prevRef.current.projects !== projects) {
      setProjects(normalized)
      prevRef.current.projects = projects
    }
    
    if (project && prevRef.current.project !== project) {
      const found = normalized.find(p => p.id === project)
      if (found) setCurrentProject(found)
      prevRef.current.project = project
    }
  }, [projects, project, setProjects, setCurrentProject])

  const isWorkspacePage = location.pathname === '/projects' && !project

  return (
    <>
      <div className="lg:hidden">
        <SidebarSection showIndicator={true}>
          <SidebarItem 
            to={buildWorkspaceUrl('/projects')}
            current={isWorkspacePage} 
            showIndicator={true} 
            onClick={closeSidebar}
          >
            <ChevronLeftIcon className={`scale-70 ${isWorkspacePage ? 'text-black' : 'text-black/70'}`} />
            <SidebarLabel className={isWorkspacePage ? 'text-black' : 'text-black/70'}>Back to Workspace</SidebarLabel>
          </SidebarItem>
        </SidebarSection>

        <SidebarDivider />
      </div>

      <SidebarSection showIndicator={true}>
        <SidebarItem to={buildUrl('/overview')} current={location.pathname === '/overview'} showIndicator={true} onClick={closeSidebar}>
          <RectangleGroupIcon className={location.pathname === '/overview' ? 'text-black' : 'text-black/70'} />
          <SidebarLabel className={location.pathname === '/overview' ? 'text-black' : 'text-black/70'}>Overview</SidebarLabel>
        </SidebarItem>
        <SidebarItem to={buildUrl('/analytics')} current={location.pathname === '/analytics'} showIndicator={true}>
          <ChartBarIcon className={location.pathname === '/analytics' ? 'text-black' : 'text-black/70'} />
          <SidebarLabel className={location.pathname === '/analytics' ? 'text-black' : 'text-black/70'}>Analytics</SidebarLabel>
        </SidebarItem>
      </SidebarSection>

      <SidebarDivider />

      <SidebarSection showIndicator={true}>
        {/* Collapsible: Site Pages */}
        <CollapsibleSidebarItem
          label="Site Pages"
          icon={<DocumentDuplicateIcon className={location.pathname.startsWith('/pages') || (!isAiCompanionMode && location.pathname.startsWith('/components')) || location.pathname.startsWith('/blocks') ? 'text-black' : 'text-black/70'} />}
          labelClassName={location.pathname.startsWith('/pages') || (!isAiCompanionMode && location.pathname.startsWith('/components')) || location.pathname.startsWith('/blocks') ? 'text-black' : 'text-black/70'}
          expanded={isSectionExpanded('sitepages')}
          onToggle={() => toggleSection('sitepages')}
          showIndicator={true}
          current={location.pathname.startsWith('/pages') || (!isAiCompanionMode && location.pathname.startsWith('/components')) || location.pathname.startsWith('/blocks')}
          closeSidebar={closeSidebar}
        >
          <SidebarItem to={buildUrl('/pages')} current={location.pathname === '/pages'}>
            <SidebarLabel className={location.pathname === '/pages' ? 'text-black' : 'text-black/70'}>Pages</SidebarLabel>
          </SidebarItem>
          {!isAiCompanionMode && (
          <SidebarItem to={buildUrl('/components')} current={location.pathname === '/components'}>
            <SidebarLabel className={location.pathname === '/components' ? 'text-black' : 'text-black/70'}>Components</SidebarLabel>
          </SidebarItem>
          )}
          {/* <SidebarItem to={buildUrl('/blocks')} current={location.pathname === '/blocks'}>
            <SidebarLabel className={location.pathname === '/blocks' ? 'text-black' : 'text-black/70'}>Blocks</SidebarLabel>
          </SidebarItem> */}
        </CollapsibleSidebarItem>

        {/* Collapsible: Blog */}
        {isAiCompanionMode ? (
          <SidebarItem to={buildUrl('/blog')} current={location.pathname.startsWith('/blog')} showIndicator={true} onClick={closeSidebar}>
            <DocumentTextIcon className={location.pathname.startsWith('/blog') ? 'text-black' : 'text-black/70'} />
            <SidebarLabel className={`${location.pathname.startsWith('/blog') ? 'text-black' : 'text-black/70'} flex items-center gap-2`}>
              <span>Blog</span>
            </SidebarLabel>
          </SidebarItem>
        ) : (
        <CollapsibleSidebarItem
          label="Blog"
          icon={<DocumentTextIcon className={location.pathname.startsWith('/blog') ? 'text-black' : 'text-black/70'} />}
          labelClassName={location.pathname.startsWith('/blog') ? 'text-black' : 'text-black/70'}
          expanded={isSectionExpanded('blog')}
          onToggle={() => toggleSection('blog')}
          showIndicator={true}
          current={location.pathname.startsWith('/blog')}
          closeSidebar={closeSidebar}
        >
          <SidebarItem to={buildUrl('/blog')} current={location.pathname === '/blog'}>
            <SidebarLabel className={location.pathname === '/blog' ? 'text-black' : 'text-black/70'}>All Posts</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/blog/drafts')} current={location.pathname === '/blog/drafts'}>
            <SidebarLabel className={location.pathname === '/blog/drafts' ? 'text-black' : 'text-black/70'}>Drafts</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/blog/published')} current={location.pathname === '/blog/published'}>
            <SidebarLabel className={location.pathname === '/blog/published' ? 'text-black' : 'text-black/70'}>Published</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/blog/archived')} current={location.pathname === '/blog/archived'}>
            <SidebarLabel className={location.pathname === '/blog/archived' ? 'text-black' : 'text-black/70'}>Archived</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/blog/tags')} current={location.pathname === '/blog/tags'}>
            <SidebarLabel className={location.pathname === '/blog/tags' ? 'text-black' : 'text-black/70'}>Tags</SidebarLabel>
          </SidebarItem>
        </CollapsibleSidebarItem>
        )}

        <SidebarItem to={buildWorkspaceUrl('/image-generation')} current={false} showIndicator={true} onClick={closeSidebar}>
          <PhotoIcon className="text-black/70" />
          <SidebarLabel className="flex min-w-0 items-center gap-2 text-black/70">
            <span className="truncate">AI Images</span>
          </SidebarLabel>
        </SidebarItem>

        <SidebarItem to={buildWorkspaceUrl('/form-builder')} current={false} showIndicator={true} onClick={closeSidebar}>
          <FormBuilderIcon data-slot="icon" className="text-black/70" />
          <SidebarLabel className="flex min-w-0 items-center gap-2 text-black/70">
            <span className="truncate">Form Builder</span>
          </SidebarLabel>
        </SidebarItem>

        {/* Collapsible: Customer & Leads */}
        <CollapsibleSidebarItem
          label="Customer & Leads"
          icon={<UsersIcon className={location.pathname.startsWith('/forms') || location.pathname.startsWith('/contacts') || location.pathname.startsWith('/orders') || location.pathname.startsWith('/products') || location.pathname.startsWith('/notifications') || location.pathname.startsWith('/webhooks') ? 'text-black' : 'text-black/70'} />}
          labelClassName={location.pathname.startsWith('/forms') || location.pathname.startsWith('/contacts') || location.pathname.startsWith('/orders') || location.pathname.startsWith('/products') || location.pathname.startsWith('/notifications') || location.pathname.startsWith('/webhooks') ? 'text-black' : 'text-black/70'}
          expanded={isSectionExpanded('customers')}
          onToggle={() => toggleSection('customers')}
          showIndicator={true}
          current={location.pathname.startsWith('/forms') || location.pathname.startsWith('/contacts') || location.pathname.startsWith('/orders') || location.pathname.startsWith('/products') || location.pathname.startsWith('/notifications') || location.pathname.startsWith('/webhooks')}
          closeSidebar={closeSidebar}
        >
          <SidebarItem to={buildUrl('/forms')} current={location.pathname.startsWith('/forms')}>
            <SidebarLabel className={location.pathname.startsWith('/forms') ? 'text-black' : 'text-black/70'}>Forms</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/contacts')} current={location.pathname.startsWith('/contacts')}>
            <SidebarLabel className={location.pathname.startsWith('/contacts') ? 'text-black' : 'text-black/70'}>Contacts</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/orders')} current={location.pathname.startsWith('/orders')}>
            <SidebarLabel className={location.pathname.startsWith('/orders') ? 'text-black' : 'text-black/70'}>Orders</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/products')} current={location.pathname.startsWith('/products')}>
            <SidebarLabel className={location.pathname.startsWith('/products') ? 'text-black' : 'text-black/70'}>Products</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/notifications')} current={location.pathname.startsWith('/notifications')}>
            <SidebarLabel className={location.pathname.startsWith('/notifications') ? 'text-black' : 'text-black/70'}>Notifications</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/webhooks')} current={location.pathname.startsWith('/webhooks')}>
            <SidebarLabel className={location.pathname.startsWith('/webhooks') ? 'text-black' : 'text-black/70'}>Webhooks</SidebarLabel>
          </SidebarItem>
        </CollapsibleSidebarItem>

        {/* Collapsible: Media & Files */}
        {/* <CollapsibleSidebarItem
          label="Media & Files"
          icon={<FolderIcon className={location.pathname.startsWith('/media') || location.pathname.startsWith('/files') ? 'text-black' : 'text-black/70'} />}
          labelClassName={location.pathname.startsWith('/media') || location.pathname.startsWith('/files') ? 'text-black' : 'text-black/70'}
          expanded={isSectionExpanded('media')}
          onToggle={() => toggleSection('media')}
          showIndicator={true}
          current={location.pathname.startsWith('/media') || location.pathname.startsWith('/files')}
        >
          <SidebarItem to={buildUrl('/media')} current={location.pathname.startsWith('/media')}>
            <SidebarLabel className={location.pathname.startsWith('/media') ? 'text-black' : 'text-black/70'}>Media Library</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/files')} current={location.pathname.startsWith('/files')}>
            <SidebarLabel className={location.pathname.startsWith('/files') ? 'text-black' : 'text-black/70'}>File Manager</SidebarLabel>
          </SidebarItem>
        </CollapsibleSidebarItem> */}

        {/* AI Chatbot*/}
        {/* <SidebarItem to={buildUrl('/ai-chatbot')} current={location.pathname.startsWith('/ai-chatbot')} showIndicator={true}>
          <ChatBubbleLeftRightIcon className={location.pathname.startsWith('/ai-chatbot') ? 'text-black' : 'text-black/70'} />
          <SidebarLabel className={`${location.pathname.startsWith('/ai-chatbot') ? 'text-black' : 'text-black/70'} flex items-center gap-2`}>
            <span>AI Chatbot</span>
            <Badge className="!text-[12px] !font-medium !rounded-md !bg-[#5C33FF] !text-white">Pro</Badge>
          </SidebarLabel>
        </SidebarItem> */}
         
         {/* Integrations */}
        <SidebarItem to={buildUrl('/integrations')} current={location.pathname.startsWith('/integrations')} showIndicator={true} onClick={closeSidebar}>
          <PuzzlePieceIcon className={location.pathname.startsWith('/integrations') ? 'text-black' : 'text-black/70'} />
          <SidebarLabel className={`${location.pathname.startsWith('/integrations') ? 'text-black' : 'text-black/70'} flex items-center gap-2`}>
            <span>Integrations</span>
            {!isProjectLicensed && <Badge className="!text-[12px] !font-medium !rounded-md !bg-[#5C33FF] !text-white">Pro</Badge>}
          </SidebarLabel>
        </SidebarItem>

        {/* Collapsible: Settings */}
        <CollapsibleSidebarItem
          label="Settings"
          icon={<Cog6ToothIcon className={location.pathname.startsWith('/seo') || location.pathname.startsWith('/site-speed') || location.pathname.startsWith('/settings') || location.pathname.startsWith('/guest-access') || location.pathname.startsWith('/domain') || location.pathname.startsWith('/wordpress') ? 'text-black' : 'text-black/70'} />}
          labelClassName={location.pathname.startsWith('/seo') || location.pathname.startsWith('/site-speed') || location.pathname.startsWith('/settings') || location.pathname.startsWith('/guest-access') || location.pathname.startsWith('/domain') || location.pathname.startsWith('/wordpress') ? 'text-black' : 'text-black/70'}
          expanded={isSectionExpanded('settings')}
          onToggle={() => toggleSection('settings')}
          showIndicator={true}
          current={location.pathname.startsWith('/seo') || location.pathname.startsWith('/site-speed') || location.pathname.startsWith('/settings') || location.pathname.startsWith('/guest-access') || location.pathname.startsWith('/domain') || location.pathname.startsWith('/wordpress')}
          closeSidebar={closeSidebar}
        >
          <SidebarItem to={buildUrl('/settings')} current={location.pathname.startsWith('/settings')}>
            <SidebarLabel className={location.pathname.startsWith('/settings') ? 'text-black' : 'text-black/70'}>General</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/guest-access')} current={location.pathname.startsWith('/guest-access')}>
            <SidebarLabel className={location.pathname.startsWith('/guest-access') ? 'text-black' : 'text-black/70'}>Guest access</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/seo')} current={location.pathname.startsWith('/seo')}>
            <SidebarLabel className={location.pathname.startsWith('/seo') ? 'text-black' : 'text-black/70'}>SEO & Meta</SidebarLabel>
          </SidebarItem>
          <SidebarItem to={buildUrl('/domain')} current={location.pathname.startsWith('/domain')}>
            <SidebarLabel className={location.pathname.startsWith('/domain') ? 'text-black' : 'text-black/70'}>
              <span>Domains</span>
              {!isProjectLicensed && <Badge className="!text-[12px] ml-2 !font-medium !rounded-md !bg-[#5C33FF] !text-white">Pro</Badge>}
            </SidebarLabel>
          </SidebarItem>
          {/* <SidebarItem to={buildUrl('/site-speed')} current={location.pathname.startsWith('/site-speed')}>
            <SidebarLabel className={location.pathname.startsWith('/site-speed') ? 'text-black' : 'text-black/70'}>
              <span>Site Speed</span>
              <Badge className="!text-[12px] ml-2 !font-medium !rounded-md !bg-[#5C33FF] !text-white">Pro</Badge>
            </SidebarLabel>
          </SidebarItem> */}
          {!isAiCompanionMode && (
          <SidebarItem to={buildUrl('/wordpress')} current={location.pathname.startsWith('/wordpress')}>
            <SidebarLabel className={location.pathname.startsWith('/wordpress') ? 'text-black' : 'text-black/70'}>
              <span>WordPress</span>
              {!isProjectLicensed && <Badge className="!text-[12px] ml-2 !font-medium !rounded-md !bg-[#5C33FF] !text-white">Pro</Badge>}
            </SidebarLabel>
          </SidebarItem>
          )}
        </CollapsibleSidebarItem>
      </SidebarSection>

      <SidebarSpacer />

      {/* AI Credits Card - hidden for shared projects */}
      {!isSharedProject && !hideCredits && (
        <div data-sidebar-extra>
          <AICreditsCard className="" />
        </div>
      )}

      {/* <SidebarSection showIndicator={true}>
        <SidebarItem to={buildUrl('/learn')} current={location.pathname.startsWith('/learn')} showIndicator={true}>
          <AcademicCapIcon className={location.pathname.startsWith('/learn') ? 'text-black' : 'text-black/70'} />
          <SidebarLabel className={location.pathname.startsWith('/learn') ? 'text-black' : 'text-black/70'}>Learn CodeDesign</SidebarLabel>
        </SidebarItem>
        <SidebarItem to={buildUrl('/changelog')} current={location.pathname.startsWith('/changelog')} showIndicator={true}>
          <NewspaperIcon className={location.pathname.startsWith('/changelog') ? 'text-black' : 'text-black/70'} />
          <SidebarLabel className={location.pathname.startsWith('/changelog') ? 'text-black' : 'text-black/70'}>Changelog</SidebarLabel>
        </SidebarItem>
      </SidebarSection> */}
    </>
  )
}
